/**
 * "Moved in BC after counting" (PHASE_pending-receipts-warning.md, amendment B).
 *
 * The posting codeunit works out BC's quantity at POSTING time, so a receipt,
 * output or consumption landing between counting a line and posting the session
 * makes the adjustment wrong by that movement. This lists the lines it happened
 * to, above the post button. **A warning only — it never blocks posting**; the
 * reviewer corrects the count with the review screen's ordinary edit.
 *
 * When the ledger has no readable system timestamp the server falls back to the
 * posting date and sends the ruled label ("Moved in BC today (time not
 * available)") plus what source was missing; both are shown as sent.
 */
import type { MovedInBc } from "../api/types";

const SOURCE_LABEL: Record<string, string> = {
  purchase_receipt: "purchase receipt",
  transfer_receipt: "transfer receipt",
  output: "output",
  consumption: "consumption",
  other: "other",
};

export function movedMarker(data: MovedInBc, itemNo: string): string | null {
  const line = data.lines.find((l) => l.item_no.trim().toUpperCase() === itemNo.trim().toUpperCase());
  if (!line) return null;
  const sign = line.net_base_qty.startsWith("-") ? "" : "+";
  return `${data.label}: ${sign}${line.net_base_qty} ${line.base_unit_of_measure ?? ""}`.trim();
}

export function MovedInBcBanner({ data, error }: { data: MovedInBc | undefined; error: string | null }) {
  if (error) {
    return (
      <div className="card notice small" data-testid="moved-error">
        <p>
          <strong>Couldn't check for movement in BC after counting.</strong> {error} This is a
          warning only; posting is not blocked.
        </p>
      </div>
    );
  }
  if (!data || data.lines.length === 0) return null;
  return (
    <div className="card notice small" role="status" data-testid="moved-banner">
      <p>
        <strong>{data.label}.</strong> BC calculates quantity when the count posts, so these
        lines will post wrong by what moved. Adjust the count above if needed — this never
        blocks posting.
      </p>
      <ul>
        {data.lines.map((line) => (
          <li key={line.item_no} data-testid={`moved-${line.item_no}`}>
            {movedMarker(data, line.item_no)}
            {Object.keys(line.by_source).length > 0 && (
              <span className="muted">
                {" "}
                (
                {Object.entries(line.by_source)
                  .map(([source, qty]) => `${SOURCE_LABEL[source] ?? source} ${qty}`)
                  .join(", ")}
                )
              </span>
            )}
          </li>
        ))}
      </ul>
      {data.missing_source && <p className="muted">{data.missing_source}</p>}
    </div>
  );
}
